/* eslint-disable  @typescript-eslint/no-explicit-any */
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import {
  AfterViewChecked,
  booleanAttribute,
  Component,
  DestroyRef,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, tap } from 'rxjs';
import { Editor } from 'tinymce';
import { StyleService } from '@vendasta/galaxy/style-service';
import { GalaxyTheme } from '@vendasta/galaxy/style-service/src/style.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

declare let tinymce: any;

@Component({
  selector: 'glxy-rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => RichTextEditorComponent),
    },
  ],
  standalone: false,
})
export class RichTextEditorComponent implements AfterViewChecked, OnDestroy, ControlValueAccessor {
  @Input() elementId = '';
  @Input({ transform: booleanAttribute }) disabled = false;
  @Input() menubar: any = false;
  @Input() statusbar = false;
  @Input() menu: any = false;
  @Input() toolbar: string[] = [
    'bold italic underline link unlink | alignleft aligncenter alignright alignjustify | bullist numlist | code removeformat',
  ];
  @Input() plugins = 'lists link code';
  @Input() invalidElements = '';
  @Input({ transform: booleanAttribute }) allowResize = true;
  private enableThemes$$ = new BehaviorSubject<boolean>(false);
  @Input() set enableThemes(enable: boolean) {
    this.enableThemes$$.next(enable);
  }

  isEditorReady = signal<boolean>(true);
  initialValue = ''; // used to store initial value, which is passed in before the editor can initialize

  private editor: Editor | undefined = undefined;
  private onChange: any;
  private onTouched: any;
  private styleService = inject(StyleService);
  private destroyRef = inject(DestroyRef);
  private theme$ = combineLatest([this.styleService.activeTheme$, this.enableThemes$$]).pipe(
    takeUntilDestroyed(),
    map(([theme, enableThemes]) => {
      if (enableThemes) {
        return theme;
      }
      return GalaxyTheme.Light;
    }),
    distinctUntilChanged(),
    tap(() => {
      this.destroyEditor();
      this.isEditorReady.set(false);
    }),
  );
  theme = toSignal(this.theme$);
  GalaxyTheme = GalaxyTheme;

  ngAfterViewChecked(): void {
    this.setupEditor();
  }

  ngOnDestroy(): void {
    this.destroyEditor();
  }

  onEditorChange(): void {
    if (!this.editor) {
      return;
    }
    const content = this.editor.getContent();
    if (this.onChange) {
      this.onChange(content);
      this.initialValue = content;
    }
  }

  writeValue(value: string): void {
    if (value === undefined) {
      return;
    }

    // need to cache this so it can set when editor is instantiated
    // writeValue() may be called before setupEditor() is begun
    this.initialValue = value;

    // propagate new value to the editor
    if (this.editor) {
      this.editor.setContent(value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setupEditor(): void {
    // in  order to edit parts of the CSS you'd need to edit the CSS files contained in the path below
    tinymce.baseURL = 'https://storage.googleapis.com/developers-vendasta-client/assets';
    tinymce.init({
      skin: this.theme() === GalaxyTheme.Dark ? 'oxide-dark' : 'oxide',
      content_css: this.theme() === GalaxyTheme.Dark ? 'dark' : 'default',
      selector: '#' + this.elementId + '-' + this.theme(),
      readonly: this.disabled,
      width: '100%',
      height: '100%',
      menubar: this.menubar,
      statusbar: this.statusbar,
      menu: this.menu,
      toolbar: this.toolbar,
      plugins: this.plugins,
      contextmenu: '',
      convert_urls: false,
      allow_conditional_comments: true,
      valid_children: '+body[style]',
      invalid_elements: this.invalidElements || null,
      branding: false,
      resize: this.allowResize,
      setup: (editor: Editor) => {
        this.editor = editor;
        editor.on('init', () => {
          if (this.initialValue) {
            this.editor?.setContent(this.initialValue);
          }
        });
        // prevents this.editor from being undefined on events
        const changeHandler = () => {
          this.onEditorChange();
        };
        editor.on('keyup', changeHandler);
        editor.on('change', changeHandler);
        editor.on('NodeChange', changeHandler);
      },
      init_instance_callback: () => {
        this.isEditorReady.set(true);
      },
    });
  }

  private destroyEditor(): void {
    tinymce.remove(this.editor);
    this.editor?.destroy();
  }
}
